// Plan A agent ground truth: AI R&D uplift, concurrent copies, and per-copy
// speed, read from the Plan A model spreadsheet
// (1fIPU1OkgnKgsCiQqcj18RXRNlqqfL1YDGCjeWAeXC7s), tab "plan-a-side-panel"
// (gid=1460401047), pulled 2026-07-09 (the copies/speed rows were re-drafted
// into a GENERAL fleet plus a small INFERENCE-SPECIALIZED fleet that runs
// much faster):
//   row 19 "AI R&D uplift (capability shaped)" (0.33-yr grid; also baked
//        into extrapolatedGraphData.json planA — replaced row 18 "(model)"
//        as ground truth July 6 2026)
//   row 21 "AI Copies (general)"                  (annual)
//   row 22 "AI Speed (tok/sec, general)"          (annual; human = 10 tok/s)
//   row 24 "AI Copies (inf. specialized)"         (annual, 2026-2040 only)
//   row 25 "AI Speed (tok/sec, inf. specialized)" (annual, 2026-2040 only)
// The 2025 copies/speed anchors come from the same model's copies-speed
// export (data/copies-speed-labor.json: 15.1M copies at 5x human).
// Re-pull: npx tsx scripts/readPlanASheet.ts --gid=1460401047

type Pair = [number, number]

// [decimal year, AI R&D uplift multiple]
export const PLAN_A_RD_UPLIFT: Pair[] = [
  [2025, 1.05822],
  [2025.33, 1.07491],
  [2025.67, 1.09517],
  [2026, 1.11973],
  [2026.33, 1.15003],
  [2026.67, 1.18746],
  [2027, 1.23349],
  [2027.33, 1.28873],
  [2027.67, 1.35402],
  [2028, 1.43209],
  [2028.33, 1.52576],
  [2028.67, 1.63899],
  [2029, 1.78443],
  [2029.33, 1.87261],
  [2029.67, 1.87261],
  [2030, 1.87261],
  [2030.33, 2.70807],
  [2030.67, 3.70297],
  [2031, 4.52415],
  [2031.33, 5.56371],
  [2031.67, 6.91029],
  [2032, 8.57484],
  [2032.33, 10.7048],
  [2032.67, 13.4613],
  [2033, 16.8694],
  [2033.33, 20.6671],
  [2033.67, 24.7102],
  [2034, 28.9939],
  [2034.33, 33.1557],
  [2034.67, 36.9471],
  [2035, 40.4719],
  [2035.33, 41.1165],
  [2035.67, 41.6719],
  [2036, 42.1662],
  [2036.33, 42.6155],
  [2036.67, 43.0301],
  [2037, 43.4169],
  [2037.33, 43.7798],
  [2037.67, 44.1191],
  [2038, 44.4362],
  [2038.33, 44.7303],
  [2038.67, 45.0035],
  [2039, 45.2565],
  [2039.33, 45.4863],
  [2039.67, 45.6932],
  [2040, 50.1971],
  [2040.33, 91.6534],
  [2040.67, 164.299],
  [2041, 285.358],
  [2041.33, 491.457],
  [2041.67, 811.365],
  [2042, 1307.82],
  [2042.33, 2098.71],
  [2042.67, 3280.68],
  [2043, 5082.19],
  [2043.33, 7929.14],
  [2043.67, 12162.9],
  [2044, 18149.6],
  [2044.33, 27151.1],
  [2044.67, 40758.5],
  [2045, 60116.2],
  [2045.33, 87125.4],
  [2045.67, 124592],
  [2046, 180530],
  [2046.33, 257620],
  [2046.67, 363891],
  [2047, 503641],
  [2047.33, 697042],
  [2047.67, 968367],
  [2048, 1.32733e+06],
  [2048.33, 1.79697e+06],
  [2048.67, 2.41448e+06],
  [2049, 3.24924e+06],
  [2049.33, 4.33253e+06],
  [2049.67, 5.7024e+06],
  [2050, 7.50161e+06],
  [3045, 7.5e8],
]

// [year, concurrent frontier-model copies on general hardware]
export const PLAN_A_COPIES: Pair[] = [
  [2025, 15145800],
  [2026.0, 3.02837e+07],
  [2027.0, 2.76535e+07],
  [2028.0, 2.34037e+07],
  [2029.0, 2.88855e+07],
  [2030.0, 4.71072e+07],
  [2031.0, 4.5945e+07],
  [2032.0, 6.30698e+07],
  [2033.0, 1.11297e+08],
  [2034.0, 1.55518e+08],
  [2035.0, 1.54313e+08],
  [2036.0, 1.49629e+08],
  [2037.0, 1.48039e+08],
  [2038.0, 1.47776e+08],
  [2039.0, 1.48277e+08],
  [2040.0, 1.50101e+08],
  [2041.0, 2e+09],
  [2042.0, 2e+10],
  [2043.0, 2e+11],
  [2044.0, 2e+12],
  [2045.0, 2e+13],
  [2046.0, 2e+14],
  [2047.0, 2e+15],
  [2048.0, 2e+16],
  [2049.0, 2e+17],
  [2050.0, 2e+18],
  [3045, 2e20],
]

// [year, per-copy speed in tokens/sec on general hardware] — the speed the
// typical copy runs at (the sheet's "general" fleet).
export const PLAN_A_SPEED_TOKS: Pair[] = [
  [2025, 50],
  [2026.0, 57],
  [2027.0, 61],
  [2028.0, 65],
  [2029.0, 67],
  [2030.0, 74],
  [2031.0, 81],
  [2032.0, 89],
  [2033.0, 98],
  [2034.0, 108],
  [2035.0, 119],
  [2036.0, 131],
  [2037.0, 144],
  [2038.0, 158],
  [2039.0, 174],
  [2040.0, 192],
  [2041.0, 288],
  [2042.0, 432],
  [2043.0, 648],
  [2044.0, 972],
  [2045.0, 1458],
  [2046.0, 2187],
  [2047.0, 3280.5],
  [2048.0, 4920.75],
  [2049.0, 7381.12],
  [2050.0, 11071.7],
  [3045, 1.11e6],
]

// [year, copies on inference-specialized hardware] — the small fast fleet.
// The sheet only defines the split 2026-2040 (row 26 zeroes it from 2041:
// the distinction retires once the whole fleet runs fast). Sample with
// sampleLogUntilSeriesEnd so the split drops out past 2040 instead of
// clamping to the 2040 value forever.
export const PLAN_A_COPIES_SPECIALIZED: Pair[] = [
  [2026.0, 7881],
  [2027.0, 23149],
  [2028.0, 59977],
  [2029.0, 123324],
  [2030.0, 213709],
  [2031.0, 458678],
  [2032.0, 1.57958e+06],
  [2033.0, 5.8971e+06],
  [2034.0, 1.55584e+07],
  [2035.0, 2.1686e+07],
  [2036.0, 2.36001e+07],
  [2037.0, 2.57771e+07],
  [2038.0, 2.80028e+07],
  [2039.0, 3.18065e+07],
  [2040.0, 3.61612e+07],
]

// [year, per-copy speed in tokens/sec on inference-specialized hardware] —
// the fastest AIs can run. Same 2026-2040 window as the copies split above.
export const PLAN_A_SPEED_TOKS_SPECIALIZED: Pair[] = [
  [2026.0, 1000],
  [2027.0, 1145],
  [2028.0, 1310],
  [2029.0, 1500],
  [2030.0, 1890],
  [2031.0, 2381],
  [2032.0, 3000],
  [2033.0, 3780],
  [2034.0, 4762],
  [2035.0, 6000],
  [2036.0, 8143],
  [2037.0, 11052],
  [2038.0, 15000],
  [2039.0, 19365],
  [2040.0, 25000],
]

// Like sampleLog but returns null past the series' last x instead of
// clamping — for the inference-specialized series, whose split genuinely
// ends in 2040 rather than holding its last value.
export function sampleLogUntilSeriesEnd(series: Pair[], x: number): number | null {
  if (x > series[series.length - 1][0]) return null
  return sampleLog(series, x)
}

// Log-linear interpolation on a sorted [x, y] series, clamped at the ends
// (all series are strictly positive and roughly exponential).
export function sampleLog(series: Pair[], x: number): number {
  if (x <= series[0][0]) return series[0][1]
  const last = series[series.length - 1]
  if (x >= last[0]) return last[1]
  for (let i = 1; i < series.length; i++) {
    const [x1, y1] = series[i]
    if (x < x1) {
      const [x0, y0] = series[i - 1]
      const t = (x - x0) / (x1 - x0)
      return Math.pow(10, Math.log10(y0) + t * (Math.log10(y1) - Math.log10(y0)))
    }
  }
  return last[1]
}
